// ── Gain & Phase Margin — Control Systems ────────────────────────────────────

const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Bode Plot showing GM and PM ────────────────────────
function DiagramBodePlot() {
  const W = 900, H = 420;

  // Magnitude plot: top half
  // Phase plot: bottom half
  // We'll show a typical second-order system Bode plot

  // Magnitude curve points (log-freq vs dB) — approximate -40dB/dec rolloff
  const magPoints = [
    [60,50],[120,48],[180,44],[220,38],[260,28],[290,14],[310,0],[330,-16],[360,-32],[400,-46],[450,-58],[500,-68],[560,-78],[620,-86],[680,-92],[740,-97]
  ];

  // Phase curve points: 0 → -180 transition
  const phasePoints = [
    [60,-10],[120,-14],[180,-20],[220,-30],[260,-50],[290,-80],[310,-108],[330,-135],[360,-155],[400,-165],[450,-170],[500,-173],[560,-175],[620,-177],[680,-178],[740,-179]
  ];

  // Convert data coords to SVG coords
  // Magnitude panel: x:[60-740] → SVG x:[80-720], y:[-100..+60] → SVG y:[30..200]
  const magX = (px) => 80 + (px - 60) / (740 - 60) * 640;
  const magY = (db) => 115 - db * (85 / 60);

  // Phase panel: x same, y:[-180..0] → SVG y:[230..390]
  const phaseX = (px) => 80 + (px - 60) / (740 - 60) * 640;
  const phaseY = (deg) => 390 + deg * (160 / 180);

  const magPoly = magPoints.map(([px,db]) => `${magX(px)},${magY(db)}`).join(' ');
  const phasePoly = phasePoints.map(([px,deg]) => `${phaseX(px)},${phaseY(deg)}`).join(' ');

  // Gain crossover: where magnitude = 0 dB → px≈310
  const gcX = magX(310);
  // Phase crossover: where phase = -180° → px≈480 (approx — let's place between 450-500)
  const pcX = magX(480);

  // Phase at gain crossover (px=310) → ~-108°
  const pmPhaseY = phaseY(-108);
  // Magnitude at phase crossover (px≈480, approx -180°) → let's interpolate magnitude ~-46 dB
  const gmMagY = magY(-46);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Bode Plot — Gain Margin and Phase Margin Visualised</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arrowG" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0, 7 4, 0 8" fill="#c0392b" opacity="0.85"/>
          </marker>
          <marker id="arrowR" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0, 7 4, 0 8" fill="#c0392b" opacity="0.85"/>
          </marker>
          <marker id="arrowO" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0, 7 4, 0 8" fill="#e07b00"/>
          </marker>
        </defs>

        {/* ── MAGNITUDE PANEL ── */}
        <rect x={70} y={20} width={660} height={185} rx={4} fill="#fff" stroke="#f0cece"/>
        <text x={76} y={14} fontSize="12" fill="#7B0D0D" fontWeight="700">MAGNITUDE (dB)</text>

        {/* Grid lines */}
        {[60,40,20,0,-20,-40,-60,-80].map(db => (
          <g key={db}>
            <line x1={80} y1={magY(db)} x2={720} y2={magY(db)} stroke="#e8f5ec" strokeWidth="1"/>
            <text x={72} y={magY(db)+4} fontSize="10" fill="#888" textAnchor="end">{db}</text>
          </g>
        ))}

        {/* 0 dB line prominent */}
        <line x1={80} y1={magY(0)} x2={720} y2={magY(0)} stroke="#aaa" strokeWidth="1.5" strokeDasharray="4 3"/>
        <text x={726} y={magY(0)+4} fontSize="11" fill="#888">0 dB</text>

        {/* Magnitude curve */}
        <polyline points={magPoly} fill="none" stroke="#c0392b" strokeWidth="2.5"/>

        {/* Gain crossover vertical line */}
        <line x1={gcX} y1={20} x2={gcX} y2={205} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="5 3"/>

        {/* Phase crossover vertical line */}
        <line x1={pcX} y1={20} x2={pcX} y2={205} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="5 3"/>

        {/* GM annotation in magnitude panel */}
        <line x1={pcX} y1={magY(0)} x2={pcX} y2={gmMagY} stroke="#e07b00" strokeWidth="2.5" markerEnd="url(#arrowO)"/>
        <line x1={pcX} y1={gmMagY} x2={pcX} y2={magY(0)} stroke="#e07b00" strokeWidth="2.5" markerEnd="url(#arrowO)"/>
        <rect x={pcX+6} y={(magY(0)+gmMagY)/2 - 12} width={88} height={22} rx={3} fill="#fff7e6" stroke="#e07b00"/>
        <text x={pcX+10} y={(magY(0)+gmMagY)/2 + 4} fontSize="12" fill="#e07b00" fontWeight="700">GM = 46 dB</text>

        {/* Gain crossover dot */}
        <circle cx={gcX} cy={magY(0)} r={5} fill="#c0392b"/>
        <text x={gcX+7} y={magY(0)-8} fontSize="11" fill="#c0392b" fontWeight="700">ω_gc</text>

        {/* ── PHASE PANEL ── */}
        <rect x={70} y={218} width={660} height={185} rx={4} fill="#fff" stroke="#f0cece"/>
        <text x={76} y={213} fontSize="12" fill="#7B0D0D" fontWeight="700">PHASE (degrees)</text>

        {/* Grid lines */}
        {[0,-45,-90,-135,-180].map(deg => (
          <g key={deg}>
            <line x1={80} y1={phaseY(deg)} x2={720} y2={phaseY(deg)} stroke="#e8f5ec" strokeWidth="1"/>
            <text x={72} y={phaseY(deg)+4} fontSize="10" fill="#888" textAnchor="end">{deg}°</text>
          </g>
        ))}

        {/* -180° line prominent */}
        <line x1={80} y1={phaseY(-180)} x2={720} y2={phaseY(-180)} stroke="#aaa" strokeWidth="1.5" strokeDasharray="4 3"/>

        {/* Phase curve */}
        <polyline points={phasePoly} fill="none" stroke="#2E75B6" strokeWidth="2.5"/>

        {/* Gain crossover vertical line in phase panel */}
        <line x1={gcX} y1={218} x2={gcX} y2={403} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="5 3"/>

        {/* Phase crossover vertical line in phase panel */}
        <line x1={pcX} y1={218} x2={pcX} y2={403} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="5 3"/>

        {/* PM annotation in phase panel */}
        <line x1={gcX} y1={phaseY(-180)} x2={gcX} y2={pmPhaseY} stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#arrowR)"/>
        <line x1={gcX} y1={pmPhaseY} x2={gcX} y2={phaseY(-180)} stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#arrowR)"/>
        <rect x={gcX+6} y={(phaseY(-180)+pmPhaseY)/2 - 12} width={82} height={22} rx={3} fill="#FEF0F0" stroke="#c0392b"/>
        <text x={gcX+10} y={(phaseY(-180)+pmPhaseY)/2 + 4} fontSize="12" fill="#c0392b" fontWeight="700">PM = 72°</text>

        {/* Phase crossover dot */}
        <circle cx={pcX} cy={phaseY(-173)} r={5} fill="#e07b00"/>
        <text x={pcX+7} y={phaseY(-173)+14} fontSize="11" fill="#e07b00" fontWeight="700">ω_pc</text>

        {/* Gain crossover dot on phase */}
        <circle cx={gcX} cy={pmPhaseY} r={5} fill="#c0392b"/>

        {/* ── LEGEND ── */}
        <rect x={730} y={30} width={155} height={355} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={807} y={50} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Legend</text>

        {[
          { y:62, color:'#c0392b', label:'Magnitude |G(jω)|', sub:'dB vs log frequency' },
          { y:102, color:'#2E75B6', label:'Phase ∠G(jω)', sub:'degrees vs log freq' },
          { y:142, color:'#c0392b', label:'Gain crossover ω_gc', sub:'|G|=0 dB; PM measured here' },
          { y:182, color:'#e07b00', label:'Phase crossover ω_pc', sub:'∠G=−180°; GM measured here' },
        ].map(({y, color, label, sub}) => (
          <g key={y}>
            <rect x={736} y={y} width={143} height={36} rx={3} fill="#fff5f5" stroke={color} strokeWidth="1"/>
            <text x={742} y={y+14} fontSize="11" fill={color} fontWeight="700">{label}</text>
            <text x={742} y={y+28} fontSize="10" fill="#555">{sub}</text>
          </g>
        ))}

        {/* Frequency axis */}
        <text x={400} y={415} textAnchor="middle" fontSize="12" fill="#555" fontStyle="italic">← log(ω)  Frequency (rad/s) →</text>

        {/* Axis labels */}
        <text x={82} y={115} fontSize="10" fill="#ccc">ω→</text>
      </svg>
      <div style={S.dCaption}>
        A Bode plot shows magnitude (top) and phase (bottom) vs. log frequency. The gain crossover frequency ω_gc (red dashed) is where |G(jω)| = 0 dB — Phase Margin is read here. The phase crossover frequency ω_pc (orange dashed) is where ∠G(jω) = −180° — Gain Margin is read here. Both margins must be positive for stability.
      </div>
    </div>
  );
}

// ── Diagram 2: Nyquist Plot with GM and PM ────────────────────────
function DiagramNyquist() {
  const W = 900, H = 400;
  const cx = 310, cy = 200; // origin
  const scale = 120; // pixels per unit

  // A typical open-loop Nyquist contour for a stable system
  // Parametric: for ω from 0 to ∞, G(jω) traces a curve in complex plane
  // Using: G(s) = 1 / (s(s+1)(s+2)) type shape
  // Pre-computed key points (Re, Im):
  const nyqPoints = [
    [2.2, -0.05],   // ω very small → large positive real
    [1.8, -0.2],
    [1.2, -0.5],
    [0.7, -0.8],
    [0.2, -0.9],
    [-0.2, -0.75],
    [-0.45, -0.5],
    [-0.5, -0.3],   // approaches -1/GM on real axis
    [-0.48, -0.15],
    [-0.42, -0.05],
    [-0.35, 0.0],   // crosses real axis (phase crossover)
    [-0.25, 0.04],
    [-0.15, 0.05],
    [-0.08, 0.04],
    [0.0, 0.01],    // ω→∞
  ];

  const toSVG = ([re, im]) => [cx + re * scale, cy - im * scale];

  const pathD = nyqPoints.map(([re, im], i) => {
    const [x, y] = toSVG([re, im]);
    return i === 0 ? `M ${x},${y}` : `L ${x},${y}`;
  }).join(' ');

  // Critical point (-1, 0)
  const [critX, critY] = toSVG([-1, 0]);

  // Phase crossover: where curve crosses real axis, at about (-0.35, 0) → this is -1/GM
  const [pcX, pcY] = toSVG([-0.35, 0]);

  // Gain crossover: point on unit circle closest to origin where |G|=1
  // Approx where nyq curve has magnitude 1: at about (0.2, -0.9) → magnitude ≈ √(0.04+0.81)≈0.92 ≈ unit
  // Let's use ~(0.15, -0.95)
  const [gcX, gcY] = toSVG([0.18, -0.9]);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Nyquist Plot — Gain Margin and Phase Margin in the Complex Plane</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="nyqArrow" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b" opacity="0.8"/>
          </marker>
        </defs>

        {/* Main plot area */}
        <rect x={50} y={20} width={580} height={360} rx={4} fill="#fff" stroke="#f0cece"/>

        {/* Grid */}
        {[-2,-1,0,1,2].map(v => (
          <g key={`gv${v}`}>
            <line x1={cx+v*scale} y1={20} x2={cx+v*scale} y2={380} stroke="#eee" strokeWidth="1"/>
            <text x={cx+v*scale} y={390} textAnchor="middle" fontSize="10" fill="#aaa">{v}</text>
          </g>
        ))}
        {[-1,0,1].map(v => (
          <g key={`gh${v}`}>
            <line x1={50} y1={cy-v*scale} x2={630} y2={cy-v*scale} stroke="#eee" strokeWidth="1"/>
            <text x={44} y={cy-v*scale+4} textAnchor="end" fontSize="10" fill="#aaa">{v > 0 ? `+${v}j` : v < 0 ? `${v}j` : '0'}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={55} y1={cy} x2={625} y2={cy} stroke="#bbb" strokeWidth="1.5"/>
        <line x1={cx} y1={25} x2={cx} y2={375} stroke="#bbb" strokeWidth="1.5"/>
        <text x={630} y={cy+4} fontSize="11" fill="#555">Re</text>
        <text x={cx+4} y={28} fontSize="11" fill="#555">Im</text>

        {/* Unit circle */}
        <circle cx={cx} cy={cy} r={scale} fill="none" stroke="#f0cece" strokeWidth="1.5" strokeDasharray="6 4"/>
        <text x={cx+scale+4} y={cy-4} fontSize="10" fill="#aaa">|G|=1</text>

        {/* Nyquist curve */}
        <path d={pathD} fill="none" stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#nyqArrow)"/>

        {/* Reflection (conjugate, ω<0, dashed) */}
        <path d={[...nyqPoints].reverse().map(([re, im], i) => {
          const [x, y] = toSVG([re, -im]);
          return i === 0 ? `M ${x},${y}` : `L ${x},${y}`;
        }).join(' ')} fill="none" stroke="#c0392b" strokeWidth="1.5" strokeDasharray="5 3" opacity="0.4"/>

        {/* Critical point (-1, 0) */}
        <circle cx={critX} cy={critY} r={8} fill="none" stroke="#c0392b" strokeWidth="2"/>
        <circle cx={critX} cy={critY} r={3} fill="#c0392b"/>
        <text x={critX-6} y={critY-14} fontSize="12" fill="#c0392b" fontWeight="700">(−1, 0)</text>
        <text x={critX-6} y={critY-2} fontSize="10" fill="#c0392b">critical point</text>

        {/* Phase crossover point on real axis */}
        <circle cx={pcX} cy={pcY} r={5} fill="#e07b00"/>
        <text x={pcX+6} y={pcY-8} fontSize="11" fill="#e07b00" fontWeight="700">−1/GM</text>

        {/* Arrow from critical to phase crossover = GM */}
        <line x1={critX} y1={cy-8} x2={pcX} y2={cy-8} stroke="#e07b00" strokeWidth="2" strokeDasharray="3 2"/>
        <text x={(critX+pcX)/2} y={cy-14} textAnchor="middle" fontSize="11" fill="#e07b00" fontWeight="700">GM</text>

        {/* Gain crossover point */}
        <circle cx={gcX} cy={gcY} r={5} fill="#c0392b"/>

        {/* PM: angle from negative real axis to vector from origin to gain crossover */}
        {/* Draw vector from origin to gain crossover */}
        <line x1={cx} y1={cy} x2={gcX} y2={gcY} stroke="#c0392b" strokeWidth="2" strokeDasharray="4 2"/>
        {/* Negative real axis reference */}
        <line x1={cx} y1={cy} x2={critX-10} y2={cy} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="3 2" opacity="0.5"/>
        {/* Arc for PM angle */}
        <path d={`M ${cx - 55},${cy} A 55,55 0 0,0 ${gcX - cx + cx - 10},${gcY - cy + cy + 12}`}
          fill="none" stroke="#c0392b" strokeWidth="1.5" opacity="0.7"/>
        <text x={cx - 90} y={cy + 38} fontSize="12" fill="#c0392b" fontWeight="700">PM</text>

        {/* Annotations */}
        <text x={nyqPoints[0][0]*scale+cx+4} y={cy-nyqPoints[0][1]*scale-6} fontSize="11" fill="#c0392b" fontWeight="700">ω→0</text>
        <text x={cx+8} y={cy-10} fontSize="11" fill="#c0392b" fontWeight="700">ω→∞</text>

        {/* Right info panel */}
        <rect x={640} y={20} width={245} height={360} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={762} y={40} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Nyquist Stability</text>

        {[
          { y:52, bg:'#fff5f5', bc:'#c0392b', lines:['Nyquist curve','Path of G(jω) as ω: 0→∞','in the complex plane'] },
          { y:108, bg:'#FEF0F0', bc:'#c0392b', lines:['Critical point (−1, 0)','System is unstable if Nyquist','encircles this point CW'] },
          { y:164, bg:'#fff7e6', bc:'#e07b00', lines:['Gain Margin (GM)','Distance from (−1,0) to where','curve crosses negative real axis'] },
          { y:220, bg:'#FEF0F0', bc:'#c0392b', lines:['Phase Margin (PM)','Angle at unit-circle crossing','between curve & −real axis'] },
          { y:276, bg:'#fff5f5', bc:'#c0392b', lines:['Stability condition','Both GM and PM must be','positive for closed-loop stability'] },
          { y:332, bg:'#eaf3ff', bc:'#2E75B6', lines:['Dashed curve','Conjugate (ω<0) — mirror','image of ω>0 curve'] },
        ].map(({y, bg, bc, lines}) => (
          <g key={y}>
            <rect x={646} y={y} width={233} height={52} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            {lines.map((t, i) => (
              <text key={i} x={652} y={y + 16 + i*14} fontSize={i===0?'12':'11'} fill={i===0?bc:'#444'} fontWeight={i===0?'700':'400'}>{t}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The Nyquist plot traces the open-loop frequency response G(jω) in the complex plane. Gain Margin is how much gain can increase before the curve reaches (−1, 0). Phase Margin is the angle at the unit-circle crossing. The system is stable when the Nyquist curve does not encircle the critical point (−1, 0) clockwise.
      </div>
    </div>
  );
}

// ── Diagram 3: Gain Margin — what happens when you push it ──────────
function DiagramStabilityRegions() {
  const W = 900, H = 340;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Effect of Gain on Stability — From Overdamped to Unstable</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Three panels */}
        {[
          { x:10,  label:'Low Gain  (GM large)', color:'#c0392b', bg:'#FEF0F0', desc:'Sluggish, overdamped', response:[[0,0],[15,0.3],[30,0.65],[45,0.82],[60,0.90],[80,0.94],[100,0.96],[130,0.98],[160,0.99]] },
          { x:305, label:'Optimal Gain (good GM & PM)', color:'#2E75B6', bg:'#eaf3ff', desc:'Fast, well-damped', response:[[0,0],[10,0.5],[20,0.9],[30,1.05],[40,1.02],[55,1.00],[80,1.00],[130,1.00],[160,1.00]] },
          { x:600, label:'Excessive Gain (GM ≈ 0)', color:'#c0392b', bg:'#FEF0F0', desc:'Oscillatory / unstable', response:[[0,0],[8,0.8],[16,1.2],[24,0.6],[32,1.3],[40,0.5],[55,1.35],[75,0.4],[100,1.4],[130,0.3],[160,1.4]] },
        ].map(({ x, label, color, bg, desc, response }) => {
          const pw = 275, ph = 200;
          const ry = 60, rh = 180;
          const toRX = (t) => x + 16 + (t/160) * (pw-32);
          const toRY = (v) => ry + rh - v * rh;
          const pts = response.map(([t,v]) => `${toRX(t)},${toRY(v)}`).join(' ');

          return (
            <g key={x}>
              <rect x={x} y={10} width={pw} height={ph+80} rx={5} fill={bg} stroke={color} strokeWidth="1.5"/>
              <text x={x+pw/2} y={30} textAnchor="middle" fontSize="13" fill={color} fontWeight="700">{label}</text>
              <text x={x+pw/2} y={46} textAnchor="middle" fontSize="11" fill="#666" fontStyle="italic">{desc}</text>

              {/* Plot area */}
              <rect x={x+14} y={ry-5} width={pw-28} height={rh+10} rx={2} fill="#fff" stroke="#ddd"/>

              {/* Reference = 1 line */}
              <line x1={x+14} y1={toRY(1)} x2={x+pw-14} y2={toRY(1)} stroke={color} strokeWidth="1" strokeDasharray="4 3" opacity="0.5"/>
              <text x={x+pw-16} y={toRY(1)-4} textAnchor="end" fontSize="9" fill={color} opacity="0.7">reference</text>

              {/* Step response curve */}
              <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5"/>

              {/* Axes */}
              <line x1={x+14} y1={ry+rh} x2={x+pw-14} y2={ry+rh} stroke="#bbb" strokeWidth="1"/>
              <line x1={x+14} y1={ry-5} x2={x+14} y2={ry+rh} stroke="#bbb" strokeWidth="1"/>
              <text x={x+pw/2} y={ry+rh+14} textAnchor="middle" fontSize="9" fill="#aaa">Time →</text>
              <text x={x+6} y={ry+rh/2} fontSize="9" fill="#aaa" transform={`rotate(-90,${x+6},${ry+rh/2})`}>Output</text>

              {/* Info box */}
              <rect x={x+10} y={270} width={pw-20} height={52} rx={3} fill="#fff" stroke={color} strokeWidth="1" opacity="0.9"/>
              <text x={x+16} y={287} fontSize="11" fill={color} fontWeight="700">{
                x===10 ? 'PM large, GM large' : x===305 ? 'PM ≈ 45–60°, GM ≥ 6 dB' : 'PM < 0° or GM < 0 dB'
              }</text>
              <text x={x+16} y={303} fontSize="10" fill="#555">{
                x===10 ? 'System too slow to respond' : x===305 ? 'Typical design target range' : 'Sustained or growing oscillation'
              }</text>
              <text x={x+16} y={318} fontSize="10" fill="#555">{
                x===10 ? 'Increase gain for faster response' : x===305 ? 'Good balance of speed & stability' : 'Reduce gain or add compensation'
              }</text>
            </g>
          );
        })}

      </svg>
      <div style={S.dCaption}>Step responses for three gain levels. Too little gain → slow sluggish response. Optimal gain → fast, well-damped with good margins. Excessive gain → oscillatory or unstable. Both Gain Margin and Phase Margin quantify how far from the boundary the system sits.</div>
    </div>
  );
}

// ── Diagram 4: PM and GM on a Bode — annotated with rules ──────────
function DiagramMarginAnnotated() {
  const W = 900, H = 310;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Reading Gain & Phase Margin — Step-by-Step Annotation</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Background */}
        <rect x={10} y={10} width={880} height={290} rx={5} fill="#fff" stroke="#f0cece"/>

        {/* Step labels on left */}
        {[
          { y:45, num:'1', color:'#c0392b', text:'Draw |G(jω)| on top (magnitude in dB vs log ω)' },
          { y:85, num:'2', color:'#c0392b', text:'Draw ∠G(jω) on bottom (phase in degrees vs log ω)' },
          { y:125, num:'3', color:'#c0392b', text:'Find ω_gc: where magnitude plot crosses 0 dB' },
          { y:165, num:'#E07B00', color:'#e07b00', text:'Find ω_pc: where phase plot crosses −180°' },
          { y:205, num:'4', color:'#c0392b', text:'Phase Margin = phase at ω_gc  MINUS  (−180°)' },
          { y:245, num:'5', color:'#e07b00', text:'Gain Margin = 0 dB  MINUS  magnitude at ω_pc  [in dB]' },
        ].map(({ y, num, color, text }, i) => (
          <g key={y}>
            <circle cx={36} cy={y} r={14} fill={color} opacity={0.15}/>
            <circle cx={36} cy={y} r={14} fill="none" stroke={color} strokeWidth="1.5"/>
            <text x={36} y={y+5} textAnchor="middle" fontSize="12" fill={color} fontWeight="700">{i+1}</text>
            <text x={58} y={y+5} fontSize="13" fill="#222">{text}</text>
          </g>
        ))}

        {/* Visual mini bode on right side */}
        {(() => {
          const bx = 560, by = 20, bw = 310, bh = 130;
          const px = 560, py = 165, ph = 130;

          // Magnitude curve
          const mpts = [[bx+10,by+20],[bx+60,by+25],[bx+110,by+40],[bx+150,by+65],[bx+185,by+65],[bx+220,by+85],[bx+270,by+105],[bx+300,by+120]];
          // Phase curve
          const ppts = [[px+10,py+10],[px+60,py+15],[px+110,py+30],[px+150,py+62],[px+185,py+85],[px+220,py+110],[px+270,py+125],[px+300,py+128]];

          // 0 dB line at ~by+65 (mid-magnitude)
          const zeroDB = by+65;
          // -180 line at ~py+100
          const neg180 = py+100;

          // GC crossing at x≈185
          const gcX = bx+185;
          // PC crossing at x≈240
          const pcX = bx+240;

          // Phase at gcX — interpolate ppts near gcX
          const phaseAtGC = py+95;
          // Magnitude at pcX
          const magAtPC = by+92;

          return (
            <g>
              {/* Magnitude panel */}
              <rect x={bx} y={by} width={bw} height={bh} rx={3} fill="#fffafa" stroke="#f0cece"/>
              <text x={bx+bw/2} y={by+12} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">|G(jω)| dB</text>
              {/* 0dB line */}
              <line x1={bx+5} y1={zeroDB} x2={bx+bw-5} y2={zeroDB} stroke="#aaa" strokeWidth="1" strokeDasharray="3 2"/>
              <text x={bx+bw-8} y={zeroDB-3} fontSize="9" fill="#888" textAnchor="end">0 dB</text>
              {/* Magnitude curve */}
              <polyline points={mpts.map(([x,y])=>`${x},${y}`).join(' ')} fill="none" stroke="#c0392b" strokeWidth="2"/>
              {/* GC vertical */}
              <line x1={gcX} y1={by} x2={gcX} y2={by+bh} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4 2"/>
              {/* PC vertical */}
              <line x1={pcX} y1={by} x2={pcX} y2={by+bh} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4 2"/>
              {/* GC dot */}
              <circle cx={gcX} cy={zeroDB} r={4} fill="#c0392b"/>
              {/* GM bracket */}
              <line x1={pcX} y1={zeroDB} x2={pcX} y2={magAtPC} stroke="#e07b00" strokeWidth="2"/>
              <text x={pcX+4} y={(zeroDB+magAtPC)/2+4} fontSize="10" fill="#e07b00" fontWeight="700">GM</text>

              {/* Phase panel */}
              <rect x={px} y={py} width={bw} height={ph} rx={3} fill="#fffafa" stroke="#f0cece"/>
              <text x={px+bw/2} y={py+12} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">∠G(jω)°</text>
              {/* -180 line */}
              <line x1={px+5} y1={neg180} x2={px+bw-5} y2={neg180} stroke="#aaa" strokeWidth="1" strokeDasharray="3 2"/>
              <text x={px+bw-8} y={neg180-3} fontSize="9" fill="#888" textAnchor="end">−180°</text>
              {/* Phase curve */}
              <polyline points={ppts.map(([x,y])=>`${x},${y}`).join(' ')} fill="none" stroke="#2E75B6" strokeWidth="2"/>
              {/* GC vertical */}
              <line x1={gcX} y1={py} x2={gcX} y2={py+ph} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4 2"/>
              {/* PC vertical */}
              <line x1={pcX} y1={py} x2={pcX} y2={py+ph} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4 2"/>
              {/* PM bracket */}
              <line x1={gcX} y1={neg180} x2={gcX} y2={phaseAtGC} stroke="#c0392b" strokeWidth="2"/>
              <text x={gcX+4} y={(neg180+phaseAtGC)/2+4} fontSize="10" fill="#c0392b" fontWeight="700">PM</text>
              {/* Dots */}
              <circle cx={gcX} cy={phaseAtGC} r={4} fill="#c0392b"/>
              <circle cx={pcX} cy={neg180} r={4} fill="#e07b00"/>

              <text x={bx+bw/2} y={py+ph+14} textAnchor="middle" fontSize="9" fill="#aaa">log ω →</text>
            </g>
          );
        })()}
      </svg>
      <div style={S.dCaption}>To read margins from a Bode plot: (1) find ω_gc where magnitude = 0 dB — read phase at that frequency; Phase Margin = that phase + 180°. (2) Find ω_pc where phase = −180° — read magnitude at that frequency; Gain Margin = −(that magnitude in dB).</div>
    </div>
  );
}

// ── Diagram 5: Typical design targets & stability zones ───────────
function DiagramTargetZones() {
  const W = 900, H = 360;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Design Target Zones — GM and PM Stability Map</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Background axes area */}
        <rect x={70} y={20} width={580} height={310} rx={4} fill="#fff" stroke="#f0cece"/>

        {/* Axes */}
        <line x1={80} y1={320} x2={640} y2={320} stroke="#555" strokeWidth="1.5"/>
        <line x1={80} y1={320} x2={80} y2={30} stroke="#555" strokeWidth="1.5"/>
        <text x={360} y={345} textAnchor="middle" fontSize="12" fill="#555">Phase Margin (degrees)</text>
        <text x={20} y={175} textAnchor="middle" fontSize="12" fill="#555" transform="rotate(-90,20,175)">Gain Margin (dB)</text>

        {/* X axis ticks: PM 0–90 */}
        {[0,15,30,45,60,75,90].map(pm => {
          const x = 80 + (pm/90)*560;
          return (
            <g key={pm}>
              <line x1={x} y1={320} x2={x} y2={326} stroke="#555" strokeWidth="1"/>
              <text x={x} y={336} textAnchor="middle" fontSize="10" fill="#555">{pm}°</text>
            </g>
          );
        })}

        {/* Y axis ticks: GM 0–30 dB */}
        {[0,5,10,15,20,25,30].map(gm => {
          const y = 320 - (gm/30)*290;
          return (
            <g key={gm}>
              <line x1={74} y1={y} x2={80} y2={y} stroke="#555" strokeWidth="1"/>
              <text x={68} y={y+4} textAnchor="end" fontSize="10" fill="#555">{gm}</text>
            </g>
          );
        })}

        {/* Stability zones */}
        {/* Unstable zone: PM < 0 or GM < 0 — show left/bottom strip */}
        <rect x={80} y={30} width={(0/90)*560+1} height={290} fill="#ffebee" opacity="0.7"/>

        {/* Marginally stable: PM 0-30 or GM 0-6 */}
        <rect x={80} y={320-(6/30)*290} width={560} height={(6/30)*290} fill="#fff3e0" opacity="0.5"/>
        <rect x={80} y={30} width={(30/90)*560} height={290} fill="#fff3e0" opacity="0.5"/>

        {/* Recommended zone: PM 45-60, GM 8-20 */}
        {(() => {
          const x1 = 80 + (45/90)*560;
          const x2 = 80 + (60/90)*560;
          const y1 = 320 - (20/30)*290;
          const y2 = 320 - (8/30)*290;
          return (
            <>
              <rect x={x1} y={y1} width={x2-x1} height={y2-y1} fill="#c0392b" opacity="0.15" rx={4}/>
              <rect x={x1} y={y1} width={x2-x1} height={y2-y1} fill="none" stroke="#c0392b" strokeWidth="2" strokeDasharray="5 3" rx={4}/>
              <text x={(x1+x2)/2} y={(y1+y2)/2-6} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">RECOMMENDED</text>
              <text x={(x1+x2)/2} y={(y1+y2)/2+10} textAnchor="middle" fontSize="10" fill="#c0392b">PM: 45–60°, GM: 8–20 dB</text>
            </>
          );
        })()}

        {/* Typical textbook minimum: PM≥45, GM≥6 */}
        {(() => {
          const x1 = 80 + (45/90)*560;
          const y1 = 320 - (6/30)*290;
          return (
            <>
              <line x1={x1} y1={30} x2={x1} y2={320} stroke="#c0392b" strokeWidth="2" strokeDasharray="7 4"/>
              <line x1={80} y1={y1} x2={640} y2={y1} stroke="#c0392b" strokeWidth="2" strokeDasharray="7 4"/>
              <text x={x1+4} y={310} fontSize="10" fill="#c0392b" fontWeight="700">PM=45°</text>
              <text x={86} y={y1-4} fontSize="10" fill="#c0392b" fontWeight="700">GM=6 dB</text>
              <text x={86} y={y1-17} fontSize="10" fill="#c0392b">minimum</text>
            </>
          );
        })()}

        {/* Scatter dots — typical designs */}
        {[
          [52,12,'#c0392b','Typical servo'], [58,15,'#c0392b','Audio amp'],
          [45,8,'#2E75B6','Robot arm'], [60,20,'#2E75B6','PID position'],
          [30,5,'#e07b00','Marginal'], [20,3,'#c0392b','Near unstable'],
          [70,25,'#c0392b','Conservative'], [48,10,'#2E75B6','Process ctrl'],
        ].map(([pm, gm, color, name]) => {
          const x = 80 + (pm/90)*560;
          const y = 320 - (gm/30)*290;
          return (
            <g key={name}>
              <circle cx={x} cy={y} r={6} fill={color} opacity="0.8"/>
              <text x={x+8} y={y+4} fontSize="9" fill={color}>{name}</text>
            </g>
          );
        })}

        {/* Legend */}
        <rect x={660} y={20} width={225} height={330} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={772} y={40} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Zone Guide</text>

        {[
          { y:52, bg:'#ffebee', bc:'#c0392b', lines:['Unstable / Critical','PM < 0° or GM < 0 dB','Avoid at all costs'] },
          { y:108, bg:'#fff3e0', bc:'#e07b00', lines:['Marginal','PM 0–30° or GM 0–6 dB','Acceptable only for slow systems'] },
          { y:164, bg:'#eaf3ff', bc:'#2E75B6', lines:['Acceptable','PM 30–45°, GM 6–8 dB','Most textbook standards'] },
          { y:220, bg:'#FEF0F0', bc:'#c0392b', lines:['Recommended ✓','PM 45–60°, GM 8–20 dB','Best balance of speed & stability'] },
          { y:276, bg:'#fff5f5', bc:'#aaa', lines:['Conservative','PM > 60°, GM > 20 dB','Very stable, possibly sluggish'] },
        ].map(({ y, bg, bc, lines }) => (
          <g key={y}>
            <rect x={666} y={y} width={213} height={52} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            {lines.map((t, i) => (
              <text key={i} x={672} y={y + 16 + i*14} fontSize={i===0?'12':'10'} fill={i===0?bc:'#444'} fontWeight={i===0?'700':'400'}>{t}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Stability map showing the recommended design region (green box). Minimum acceptable: PM ≥ 45°, GM ≥ 6 dB (red dashed lines). The recommended zone (PM 45–60°, GM 8–20 dB) gives a good balance between transient performance and stability robustness. Conservative margins sacrifice speed for safety.</div>
    </div>
  );
}

// ── Diagram 6: Root locus showing how gain affects poles ──────────
function DiagramRootLocus() {
  const W = 900, H = 360;
  const cx = 320, cy = 180;
  const sc = 60;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Root Locus — How Increasing Gain Moves Poles Toward Instability</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="rlArrow" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
        </defs>

        {/* Plot area */}
        <rect x={50} y={20} width={530} height={320} rx={4} fill="#fff" stroke="#f0cece"/>

        {/* Grid */}
        {[-4,-3,-2,-1,0,1].map(v => (
          <g key={v}>
            <line x1={cx+v*sc} y1={20} x2={cx+v*sc} y2={340} stroke="#f0f0f0" strokeWidth="1"/>
            <text x={cx+v*sc} y={350} textAnchor="middle" fontSize="10" fill="#bbb">{v}</text>
          </g>
        ))}
        {[-2,-1,0,1,2].map(v => (
          <g key={`j${v}`}>
            <line x1={50} y1={cy-v*sc} x2={580} y2={cy-v*sc} stroke="#f0f0f0" strokeWidth="1"/>
            {v!==0 && <text x={46} y={cy-v*sc+4} textAnchor="end" fontSize="10" fill="#bbb">{v>0?`+${v}j`:`${v}j`}</text>}
          </g>
        ))}

        {/* Axes */}
        <line x1={55} y1={cy} x2={575} y2={cy} stroke="#ccc" strokeWidth="1.5"/>
        <line x1={cx} y1={25} x2={cx} y2={335} stroke="#ccc" strokeWidth="1.5"/>
        <text x={580} y={cy+4} fontSize="11" fill="#555">σ (Re)</text>
        <text x={cx+4} y={28} fontSize="11" fill="#555">jω (Im)</text>

        {/* Imaginary axis emphasis — stability boundary */}
        <line x1={cx} y1={25} x2={cx} y2={335} stroke="#c0392b" strokeWidth="2" strokeDasharray="6 4" opacity="0.6"/>
        <text x={cx+4} y={38} fontSize="11" fill="#c0392b" fontWeight="700">Stability</text>
        <text x={cx+4} y={52} fontSize="11" fill="#c0392b" fontWeight="700">boundary</text>

        {/* Open-loop poles (×) at -1, -2, -3 */}
        {[-1,-2,-3].map(p => (
          <g key={p}>
            <text x={cx+p*sc} y={cy+5} textAnchor="middle" fontSize="18" fill="#e07b00" fontWeight="700">×</text>
            <text x={cx+p*sc} y={cy+20} textAnchor="middle" fontSize="10" fill="#e07b00">s={p}</text>
          </g>
        ))}

        {/* Root locus branches */}
        {/* Branch 1: -3 → -∞ (goes left, stable) */}
        <path d={`M ${cx-3*sc},${cy} L ${cx-4.5*sc},${cy}`} fill="none" stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#rlArrow)"/>

        {/* Branch 2 & 3: -1 and -2 come together at breakaway then split vertically */}
        {/* Breakaway at -1.5 */}
        <path d={`M ${cx-1*sc},${cy} L ${cx-1.5*sc},${cy}`} fill="none" stroke="#c0392b" strokeWidth="2.5"/>
        <path d={`M ${cx-2*sc},${cy} L ${cx-1.5*sc},${cy}`} fill="none" stroke="#c0392b" strokeWidth="2.5"/>

        {/* Branches going up and down from breakaway toward imaginary axis */}
        <path d={`M ${cx-1.5*sc},${cy} C ${cx-1.5*sc},${cy-40} ${cx-0.3*sc},${cy-120} ${cx+0.1*sc},${cy-2.2*sc}`}
          fill="none" stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#rlArrow)"/>
        <path d={`M ${cx-1.5*sc},${cy} C ${cx-1.5*sc},${cy+40} ${cx-0.3*sc},${cy+120} ${cx+0.1*sc},${cy+2.2*sc}`}
          fill="none" stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#rlArrow)"/>

        {/* Breakaway point marker */}
        <circle cx={cx-1.5*sc} cy={cy} r={5} fill="#2E75B6"/>
        <text x={cx-1.5*sc} y={cy-10} textAnchor="middle" fontSize="10" fill="#2E75B6" fontWeight="700">Breakaway</text>
        <text x={cx-1.5*sc} y={cy-0} textAnchor="middle" fontSize="9" fill="#2E75B6">(at s≈−1.5)</text>

        {/* Crossing point — where branches hit imaginary axis */}
        <circle cx={cx+4} cy={cy-2.2*sc} r={6} fill="#c0392b"/>
        <circle cx={cx+4} cy={cy+2.2*sc} r={6} fill="#c0392b"/>
        <text x={cx+14} y={cy-2.2*sc+4} fontSize="10" fill="#c0392b" fontWeight="700">Crosses at K=K_critical</text>
        <text x={cx+14} y={cy+2.2*sc+4} fontSize="10" fill="#c0392b" fontWeight="700">↑ GM = 0 here</text>

        {/* Gain K labels on locus */}
        <text x={cx-0.8*sc} y={cy-1.2*sc} fontSize="10" fill="#c0392b" fontStyle="italic">K increasing →</text>

        {/* Shaded unstable region */}
        <rect x={cx} y={25} width={100} height={315} fill="#ffebee" opacity="0.25"/>
        <text x={cx+50} y={180} textAnchor="middle" fontSize="12" fill="#c0392b" opacity="0.7" fontWeight="700">UNSTABLE</text>
        <text x={cx-80} y={180} textAnchor="middle" fontSize="12" fill="#c0392b" opacity="0.7" fontWeight="700">STABLE</text>

        {/* Info panel */}
        <rect x={600} y={20} width={285} height={320} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={742} y={40} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Root Locus Rules</text>

        {[
          { y:52, bg:'#fff7e6', bc:'#e07b00', lines:['Open-loop poles (×)','Start of locus (K=0)','Poles of G(s)H(s)'] },
          { y:106, bg:'#fff5f5', bc:'#c0392b', lines:['Locus moves with K','As gain K increases, closed-loop','poles trace locus branches'] },
          { y:160, bg:'#eaf3ff', bc:'#2E75B6', lines:['Breakaway point','Where two branches meet & split','Poles become complex here → oscillation'] },
          { y:214, bg:'#FEF0F0', bc:'#c0392b', lines:['Imaginary axis crossing','GM = 0 at this gain','Poles enter RHP → unstable'] },
          { y:268, bg:'#fff5f5', bc:'#c0392b', lines:['Design use','Choose gain K so poles','stay well into left-half plane'] },
        ].map(({ y, bg, bc, lines }) => (
          <g key={y}>
            <rect x={606} y={y} width={273} height={50} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            {lines.map((t, i) => (
              <text key={i} x={612} y={y + 14 + i*14} fontSize={i===0?'12':'11'} fill={i===0?bc:'#444'} fontWeight={i===0?'700':'400'}>{t}</text>
            ))}
          </g>
        ))}

        <text x={300} y={352} textAnchor="middle" fontSize="9" fill="#aaa">s-plane (σ vs jω) — arrows show direction of increasing gain K</text>
      </svg>
      <div style={S.dCaption}>Root locus shows closed-loop pole trajectories as gain K increases. Poles start at open-loop locations (×) and move along branches. As K increases, two branches break away, curve, and eventually cross the imaginary axis — at that crossing, GM = 0 and the system becomes marginally stable. GM quantifies the remaining gain before this crossing.</div>
    </div>
  );
}

// ── Main Export ───────────────────────────────────────────────────
export default function GainPhaseMargin() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Gain Margin &amp; Phase Margin — Control Systems Stability</h1>
      <p style={S.p}>
        When designing a feedback control system, the central question is not just <em>does it work</em> — but <em>how robustly stable is it?</em> A system may be stable under nominal conditions yet tip into dangerous oscillation if a component ages, a load changes, or a parameter drifts. Gain Margin (GM) and Phase Margin (PM) are the two canonical measures of that robustness. They quantify exactly how much the loop gain or the loop phase can change before stability is lost — and they are read directly from the system's frequency response on a Bode plot or a Nyquist plot.
      </p>

      <InfoBox title="Core Concepts at a Glance" items={[
        "Gain Margin (GM): how much gain can increase before the system goes unstable (read at the phase crossover frequency ω_pc where phase = −180°)",
        "Phase Margin (PM): how much additional phase lag can be tolerated before instability (read at the gain crossover frequency ω_gc where |G(jω)| = 0 dB)",
        "Both margins must be positive for a stable closed-loop system (assuming a minimum-phase plant)",
        "Typical design targets: PM ≥ 45°, GM ≥ 6 dB; recommended: PM 45–60°, GM 8–20 dB",
        "Margins are read from Bode plots (magnitude & phase vs. log frequency) or Nyquist plots (complex plane)",
        "Higher margins → more robust but often slower transient response; a trade-off always exists",
        "Root locus connects gain margins to pole locations: GM = 0 when closed-loop poles hit the imaginary axis",
      ]}/>

      <h2 style={S.h2}>What Are Gain and Phase Margin?</h2>
      <p style={S.p}>
        Consider an open-loop transfer function G(s)H(s) around the feedback path. At steady state the loop gain determines whether an error signal grows or decays. If the loop gain exceeds unity exactly when the loop has accumulated −180° of phase lag, the fed-back signal adds constructively to the input — positive feedback — and the system oscillates or diverges. The two margins measure how close we are to that edge.
      </p>
      <Table2 rows={[
        ["Gain Crossover Freq ω_gc","Frequency at which |G(jω)H(jω)| = 1 (0 dB)\nThe loop gain is unity — signal amplitude is neither growing nor decaying at this frequency"],
        ["Phase Crossover Freq ω_pc","Frequency at which ∠G(jω)H(jω) = −180°\nThe loop has accumulated enough lag to reverse the sign of the feedback"],
        ["Phase Margin (PM)","PM = ∠G(jω_gc) − (−180°) = ∠G(jω_gc) + 180°\nPositive PM means phase hasn't yet reached −180° when the gain is unity"],
        ["Gain Margin (GM)","GM = 1 / |G(jω_pc)|  →  GM(dB) = −20 log₁₀|G(jω_pc)|\nPositive GM (>0 dB) means the gain is below unity when the phase is −180°"],
        ["Stability condition","For minimum-phase systems: GM > 0 dB  AND  PM > 0°\nBoth must hold simultaneously — one alone is not sufficient"],
        ["Vector Margin","A single combined measure: smallest distance from Nyquist curve to (−1, 0)\nMore conservative than individual GM/PM; used in robust control"],
      ]}/>

      <h2 style={S.h2}>Bode Plot — Reading GM and PM</h2>
      <p style={S.p}>
        The Bode plot is the most practical tool for finding stability margins. It displays two curves against logarithmic frequency: the magnitude of the open-loop response in decibels, and its phase in degrees. Once the two critical frequencies are located, the margins are read as vertical distances on their respective curves.
      </p>
      <DiagramBodePlot/>
      <Table2 rows={[
        ["Step 1","Plot |G(jω)| in dB vs. log(ω) — magnitude Bode plot"],
        ["Step 2","Plot ∠G(jω) in degrees vs. log(ω) — phase Bode plot"],
        ["Step 3 — find PM","Locate ω_gc: where magnitude plot crosses 0 dB\nRead the phase at ω_gc. PM = (that phase) + 180°"],
        ["Step 4 — find GM","Locate ω_pc: where phase plot crosses −180°\nRead the magnitude (in dB) at ω_pc. GM = −(that magnitude)"],
        ["Interpretation","PM > 0 and GM > 0: stable.   PM < 0 or GM < 0: unstable\nLarger margins = more robust, but usually slower"],
        ["Asymptotic approximation","Bode magnitude can be sketched using straight-line asymptotes (−20 dB/decade per pole)\nAccuracy sufficient for preliminary design and margin estimation"],
        ["Units","GM in dB (0 dB means gain=1); PM in degrees\nGM can also be expressed as a ratio: GM=2 means gain can double before instability"],
      ]}/>

      <h2 style={S.h2}>Nyquist Plot — Margins in the Complex Plane</h2>
      <p style={S.p}>
        The Nyquist plot traces the open-loop frequency response G(jω)H(jω) as a curve in the complex plane. The critical point (−1, 0) represents the threshold of instability — if the Nyquist contour encircles this point clockwise, the closed-loop system has right-half-plane poles and is unstable. Gain and Phase Margin describe how far the curve stays away from (−1, 0).
      </p>
      <DiagramNyquist/>
      <Table2 rows={[
        ["Critical point (−1, 0)","The point of instability in the Nyquist plane\nCorresponds to: loop gain = 1 AND phase = −180° simultaneously"],
        ["Gain Margin (Nyquist)","GM = 1/|G(jω_pc)| = distance from origin to phase-crossover intercept on negative real axis\n1/GM gives the real-axis intercept; GM > 1 (or > 0 dB) means intercept is between 0 and −1"],
        ["Phase Margin (Nyquist)","Angle between the negative real axis and the vector from origin to unit-circle crossing\nPM is the angular gap before the curve would point directly at (−1, 0)"],
        ["Encirclement rule","Clockwise encirclements of (−1, 0) = unstable closed-loop poles (Nyquist criterion)\nFor stable open-loop plant: zero encirclements required for stability"],
        ["Conditional stability","Some systems stable only for a range of gain — both gain decrease AND increase can destabilise\nNyquist plot can cross negative real axis more than once — must check both crossings"],
        ["Non-minimum phase","Extra care needed — Nyquist criterion must account for open-loop RHP poles/zeros\nBode margins may be misleading; Nyquist provides definitive analysis"],
      ]}/>

      <h2 style={S.h2}>Effect of Gain on Transient Response</h2>
      <p style={S.p}>
        Gain Margin and Phase Margin directly shape the step response of the closed-loop system. A system with large margins tends to be sluggish and overdamped. As margins shrink (gain increases), the response becomes faster but more oscillatory. If margins go to zero, the system oscillates without decaying — and beyond that it diverges.
      </p>
      <DiagramStabilityRegions/>
      <Table2 rows={[
        ["PM and damping ratio ζ","For a second-order system: ζ ≈ PM/100  (PM in degrees, valid for PM 20°–70°)\nPM = 45° → ζ ≈ 0.45; PM = 60° → ζ ≈ 0.60 — moderate to well-damped"],
        ["PM and overshoot","Higher PM → less overshoot in step response\nPM = 45°: ~20% overshoot; PM = 60°: ~10% overshoot; PM = 90°: ~0% (overdamped)"],
        ["GM and gain sensitivity","GM of 6 dB means gain can double before instability (factor of 2)\nGM of 20 dB means gain can increase by ×10 before instability"],
        ["Bandwidth relationship","Higher gain crossover ω_gc → wider closed-loop bandwidth → faster step response\nIncreasing gain raises ω_gc but typically reduces PM — the central design trade-off"],
        ["Sensitivity peak Ms","Related to PM: Ms ≈ 1/sin(PM) for idealized systems\nMs > 2 (PM < 30°) is often considered too lightly damped for servo applications"],
      ]}/>

      <h2 style={S.h2}>How to Read Margins — Step by Step</h2>
      <DiagramMarginAnnotated/>

      <h2 style={S.h2}>Design Targets and Stability Zones</h2>
      <p style={S.p}>
        Engineering standards and textbooks converge on widely accepted ranges for stability margins. These are not arbitrary — they reflect decades of practical experience across mechanical, electrical, process, and aerospace control systems.
      </p>
      <DiagramTargetZones/>
      <Table2 rows={[
        ["Minimum acceptable","PM ≥ 30°, GM ≥ 6 dB — many textbook standards\nBelow this: transient oscillation likely; high sensitivity to parameter variation"],
        ["Recommended (servo)","PM 45–60°, GM 8–20 dB — balanced speed and robustness\nGood starting point for most feedback controller designs"],
        ["Conservative","PM > 60°, GM > 20 dB — very stable, forgiving of model error\nMay sacrifice bandwidth (speed) — appropriate for safety-critical systems"],
        ["Process control","PM 30–45° often accepted — plants are slow, model error less critical\nLarger integral action can reduce PM — must check after adding integrator"],
        ["Aerospace / automotive","Stringent requirements; often PM ≥ 45° with explicit robustness analysis\nModel uncertainty bounds used; µ-analysis or H∞ may supplement GM/PM"],
        ["When both margins matter","A system with PM = 60° but GM = 2 dB is risky — gain uncertainty alone could destabilise\nAlways check both margins independently; do not rely on one alone"],
      ]}/>

      <h2 style={S.h2}>Root Locus and the Connection to Gain Margin</h2>
      <p style={S.p}>
        The root locus method provides an alternative view: it shows how closed-loop pole locations change as gain K increases from 0 to ∞. Gain Margin is directly visible as the gain value at which a root-locus branch crosses the imaginary axis — that crossing is the exact gain at which the system becomes marginally stable.
      </p>
      <DiagramRootLocus/>
      <Table2 rows={[
        ["Root locus basics","Locus starts at open-loop poles (K=0) and ends at open-loop zeros (K→∞)\nEach branch = one closed-loop pole trajectory"],
        ["Breakaway points","Where two branches leave the real axis — poles become complex conjugates\nSystem starts oscillating; PM decreases as gain increases past this point"],
        ["Imaginary axis crossing","Exact gain at which closed-loop pole(s) cross into right-half plane\nThis is the critical gain K_c: GM = K_c / K_operating"],
        ["Routh-Hurwitz criterion","Algebraic test for stability — tells whether any roots are in RHP\nDoes not give margin directly; use with characteristic polynomial"],
        ["Using Routh for GM","Set characteristic polynomial with gain K, apply Routh array\nK_c from Routh = critical gain → GM = 20 log₁₀(K_c / K_design) dB"],
        ["Connection to Bode","Same GM/PM values are obtained from root locus, Bode, or Nyquist analysis\nThey are different representations of the same underlying physics"],
      ]}/>

      <h2 style={S.h2}>Compensation — Improving Margins</h2>
      <p style={S.p}>
        When a plant's natural margins are insufficient, a compensator (controller) is added in the forward path to reshape the frequency response and improve the margins without sacrificing bandwidth unnecessarily.
      </p>
      <Table2 rows={[
        ["Lead compensator","Adds phase lead (positive phase) near the gain crossover frequency\nC(s) = K(s+z)/(s+p), z < p → improves PM; also increases ω_gc (faster response)"],
        ["Lag compensator","Attenuates gain at high frequencies to reduce ω_gc\nC(s) = K(s+z)/(s+p), z > p → improves GM; may slow transient response"],
        ["Lead-Lag compensator","Combines both: lag for low-frequency gain, lead for improved PM\nMost versatile; used when both speed and margin improvement are needed"],
        ["PID controller","Derivative action adds phase lead (improves PM) near crossover\nIntegral action reduces GM — must account for both effects during tuning"],
        ["Notch filter","Attenuates a specific resonant frequency that threatens stability\nNarrow gain reduction at resonance; preserves PM at crossover frequency"],
        ["Loop shaping","Design approach: explicitly shape |G(jω)| to achieve desired margins and bandwidth\nModern H∞ / robust control formalises this as an optimisation problem"],
      ]}/>

      <h2 style={S.h2}>Special Cases and Limitations</h2>
      <Table2 rows={[
        ["Conditionally stable systems","Stable only for a range of gain — increasing OR decreasing gain can destabilise\nNyquist curve crosses negative real axis twice; Bode GM alone misleading"],
        ["Non-minimum phase systems","Right-half-plane zeros cause additional phase lag at all frequencies\nPM is reduced; care needed with Bode approximations; Nyquist essential"],
        ["Time delay","A pure delay e^{−Ls} adds phase lag −Lω (linearly with frequency)\nDirectly reduces PM; PM = original PM − Lω_gc × (180/π)"],
        ["Discrete-time systems","Sample/hold introduces additional phase lag ≈ −ωT_s/2 (T_s = sample period)\nRequires z-domain or sampled Bode analysis; margins tighter than continuous case"],
        ["Unstable open-loop plant","Bode margins unreliable — must use Nyquist criterion with encirclement count\nPositive PM/GM do not guarantee stability if open-loop is already unstable"],
        ["MIMO systems","GM/PM defined for each loop independently (with others closed)\nDisk margin or structured singular value µ give multi-loop robustness guarantees"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Phase Margin:  PM = ∠G(jω_gc) + 180°   where |G(jω_gc)| = 1  (0 dB)",
        "Gain Margin:   GM(dB) = −20 log₁₀|G(jω_pc)|   where ∠G(jω_pc) = −180°",
        "PM ↔ damping:  ζ ≈ PM / 100  (valid for PM in range 20°–70°, second-order approximation)",
        "PM ↔ overshoot:  %OS ≈ 100 × e^{−πζ/√(1−ζ²)}  →  decreases as PM increases",
        "Delay effect:  PM_new = PM_original − L × ω_gc × (180/π)  degrees  (L = delay in seconds)",
        "Lead compensator phase:  φ_max = sin⁻¹((α−1)/(α+1))   where α = p/z > 1",
        "Sensitivity peak:  Ms ≈ 1 / sin(PM)  (idealized; actual value from Bode or Nyquist)",
        "Gain Margin as ratio:  GM_ratio = 1 / |G(jω_pc)|  →  GM(dB) = 20 log₁₀(GM_ratio)",
      ]}/>
    </div>
  );
}